import type { ClipboardEvent } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import type { ChipProps } from '@mui/material/Chip';
import Box from '@mui/material/Box';
import type { FieldDef, FieldOption, FormValues } from '../types';
import BulkImportActions, { buildBulkImportValue, parseBulkImportText } from './BulkImportActions';
import {
  normalizeProductCode,
  ProductValidationPreview,
  useProductValidation,
} from './ProductValidationPreview';

interface TagsFieldProps {
  field: FieldDef;
  values: FormValues;
  onChange: (key: string, value: string[]) => void;
  externalError?: string;
}

const filter = createFilterOptions<FieldOption>();

function toTags(raw: string | string[] | undefined): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return raw
    .split(/[\n,;]+/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function getConflicts(tags: string[], otherRaw: string | string[] | undefined): Set<string> {
  const other = new Set(toTags(otherRaw));
  return new Set(tags.filter((t) => other.has(t)));
}

function getClipboardText(event: ClipboardEvent<HTMLElement>) {
  return event.clipboardData.getData('text').trim();
}

export default function TagsField({ field, values, onChange, externalError }: TagsFieldProps) {
  const tags = toTags(values[field.key] as string | string[] | undefined);
  const otherRaw = field.excludeValuesFrom ? (values[field.excludeValuesFrom] as string | string[] | undefined) : undefined;
  const conflicts = otherRaw !== undefined ? getConflicts(tags, otherRaw) : new Set<string>();
  const hasConflict = conflicts.size > 0;
  const conflictText = hasConflict
    ? `Valor(es) em conflito com o campo oposto: ${[...conflicts].join(', ')}`
    : undefined;
  const productValidation = useProductValidation(tags, field.key, values);
  const hasMissingProducts = productValidation.enabled && productValidation.missing.length > 0;
  const productValidationText = hasMissingProducts
    ? `${productValidation.missing.length} SKU/EAN nao encontrado(s).`
    : undefined;

  const options: FieldOption[] = field.options ?? [];
  const excludedValues = new Set(toTags(otherRaw));
  const availableOptions = options.filter((o) => !excludedValues.has(o.value));

  const selectedOptions: FieldOption[] = tags.map((t) => {
    const found = options.find((o) => o.value === t);
    return found ?? { value: t, label: t };
  });

  const handleBulkPaste = (event: ClipboardEvent<HTMLElement>) => {
    if (!field.bulkImport) return;

    const text = getClipboardText(event);
    if (!text) return;

    const result = buildBulkImportValue({
      mode: options.length > 0 ? 'options' : 'text',
      options: availableOptions,
      currentValue: tags,
      excludedValues: [...excludedValues],
      rawValues: parseBulkImportText(text),
    });

    if (!result.applied || result.value === undefined) return;

    event.preventDefault();
    onChange(field.key, Array.isArray(result.value) ? result.value : toTags(result.value));
  };

  const getChipColor = (value: string): ChipProps['color'] => {
    const normalizedValue = normalizeProductCode(value);

    if (conflicts.has(value) || productValidation.missingSet.has(normalizedValue)) return 'error';
    if (productValidation.productByCode.has(normalizedValue)) return 'success';

    return 'default';
  };

  const getChipTitle = (value: string): string | undefined => {
    const normalizedValue = normalizeProductCode(value);
    const product = productValidation.productByCode.get(normalizedValue);

    if (product) return product.nomeProduto;
    if (productValidation.missingSet.has(normalizedValue)) return 'SKU/EAN nao encontrado';

    return undefined;
  };

  return (
    <Box>
      <Autocomplete
      multiple
      freeSolo
      size="small"
      options={availableOptions}
      value={selectedOptions}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
      isOptionEqualToValue={(opt, val) => opt.value === val.value}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const inputValue = params.inputValue.trim();
        if (inputValue && !opts.some((o) => o.value === inputValue || o.label.toLowerCase() === inputValue.toLowerCase())) {
          filtered.push({ value: inputValue, label: `Adicionar "${inputValue}"` });
        }
        return filtered;
      }}
      onChange={(_e, newValue) => {
        const newTags = newValue.map((v) => {
          if (typeof v === 'string') return v;
          if (v.label.startsWith('Adicionar "')) return v.value;
          return v.value;
        });
        onChange(field.key, newTags);
      }}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((opt, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={typeof opt === 'string' ? opt : opt.label.startsWith('Adicionar "') ? opt.value : opt.label}
              size="small"
              color={getChipColor(typeof opt === 'string' ? opt : opt.value)}
              title={getChipTitle(typeof opt === 'string' ? opt : opt.value)}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.label}
          required={field.required}
          placeholder={tags.length === 0 ? (field.placeholder ?? 'Selecione ou digite') : undefined}
          error={!!externalError || hasConflict || hasMissingProducts}
          helperText={externalError ?? conflictText ?? productValidationText ?? field.helperText}
          onPaste={handleBulkPaste}
        />
      )}
      />
      <ProductValidationPreview validation={productValidation} />
      {field.bulkImport && (
        <BulkImportActions
          mode={options.length > 0 ? 'options' : 'text'}
          options={availableOptions}
          currentValue={tags}
          excludedValues={[...excludedValues]}
          onApply={(nextValue) => onChange(field.key, Array.isArray(nextValue) ? nextValue : toTags(nextValue))}
        />
      )}
    </Box>
  );
}
